// @ts-nocheck
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import MobiusAccordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import getAccordionVariantField, { AccordionVariant } from "@store/Framework/Common/Fields/Toolkit/accordionVariant";
import AnchoredAccordion from "@store/Library/Components/Toolkit/AnchoredAccordion";
import { AccordionSectionPresentationProps } from "@store/MobiusOverrides/AccordionSection/AccordionSection";
import ManagedAccordionSection from "@store/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, PropsWithChildren, useMemo } from "react";
import { css } from "styled-components";

export const enum accordionFoldingContainerFields {
    accordionHeadingLevel = "accordionHeadingLevel",
    accordionStyle = "accordionStyle",
    accordionAnchor = "accordionAnchor",
    accordionExpanded = "accordionExpanded",
    accordionTitle = "accordionTitle",
    foldInAccordion = "foldInAccordion",
    hideWhenNoResults = "hideWhenNoResults",
}

const variantField = getAccordionVariantField(accordionFoldingContainerFields.accordionStyle);

export interface AccordionFoldingContainerFieldsProps {
    [accordionFoldingContainerFields.accordionHeadingLevel]: number;
    [accordionFoldingContainerFields.accordionStyle]: AccordionVariant;
    [accordionFoldingContainerFields.accordionAnchor]: string;
    [accordionFoldingContainerFields.accordionExpanded]: boolean;
    [accordionFoldingContainerFields.accordionTitle]: string;
    [accordionFoldingContainerFields.foldInAccordion]: boolean;
    [accordionFoldingContainerFields.hideWhenNoResults]: boolean;
}

export interface AccordionFoldingContainerProps {
    /* Allows an extra condition from parent component */
    shouldShowOnlyIf?: boolean;
}

export interface AccordionFoldingContainerStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const accordionFoldingContainerStyles: AccordionFoldingContainerStyles = {
    accordion: {
        mergeCss: true,
        // By moving the accordion 1px up, we can stack two accordion widgets on top of eachother and obtain seamless display instead of having a 'duplicated' border accordionStyle.
        css: css`
            margin-top: -1px;
        `,
    },
};

type Props = AccordionFoldingContainerProps & AccordionFoldingContainerFieldsProps;

const AccordionFoldingContainer: FC<PropsWithChildren<Props>> = ({
    accordionHeadingLevel = 0,
    accordionStyle,
    accordionAnchor,
    accordionExpanded,
    accordionTitle,
    foldInAccordion,
    hideWhenNoResults,
    shouldShowOnlyIf = true,
    children,
}) => {
    const accordionStyles = useMemo(() => {
        return mergeProps<AccordionFoldingContainerStyles>(accordionFoldingContainerStyles, {
            accordionSection: variantField.parse(accordionStyle),
        });
    }, [accordionFoldingContainerStyles, accordionStyle, variantField]);

    if (hideWhenNoResults && !shouldShowOnlyIf) {
        return null;
    }

    if (!foldInAccordion) {
        return children;
    }

    const accordionSection = () => {
        if (accordionAnchor) {
            return (
                <AnchoredAccordion
                    anchorId={accordionAnchor}
                    initialExpanded={accordionExpanded}
                    title={accordionTitle}
                    {...accordionStyles.accordionSection}
                >
                    {children}
                </AnchoredAccordion>
            );
        }
        return (
            <ManagedAccordionSection
                initialExpanded={accordionExpanded}
                title={accordionTitle}
                {...accordionStyles.accordionSection}
            >
                {children}
            </ManagedAccordionSection>
        );
    };

    return (
        <MobiusAccordion headingLevel={accordionHeadingLevel} {...accordionStyles.accordion}>
            {accordionSection()}
        </MobiusAccordion>
    );
};

export const getAccordionFoldingContainerTab = (displayName: string = "Accordion", sortOrder: number = 1) => ({
    displayName,
    sortOrder,
});

export const accordionFoldingContainerFieldDefinitions: FieldDefinition[] = [
    {
        name: accordionFoldingContainerFields.foldInAccordion,
        displayName: "Fold In Accordion",
        fieldType: "General",
        editorTemplate: "CheckboxField",
        defaultValue: false,
    },
    {
        name: accordionFoldingContainerFields.accordionHeadingLevel,
        displayName: "Heading Level",
        editorTemplate: "IntegerField",
        fieldType: "Contextual",
        defaultValue: null,
        tooltip:
            "Semantic heading level for accessibility (1-6), only set if accordions are to be treated as page headings",
        validate: x => (typeof x === "number" && (x < 1 || x > 6) ? "Value must be between 1 and 6" : ""),
        isVisible: x => x.fields.foldInAccordion,
    },
    {
        ...variantField.definition,
        displayName: "Style",
        fieldType: "Contextual",
        isVisible: x => x.fields.foldInAccordion,
    },
    {
        name: accordionFoldingContainerFields.accordionAnchor,
        displayName: "Anchor",
        fieldType: "General",
        editorTemplate: "TextField",
        defaultValue: "",
        isVisible: x => x.fields.foldInAccordion,
    },
    {
        name: accordionFoldingContainerFields.accordionExpanded,
        displayName: "Expand by default",
        fieldType: "General",
        editorTemplate: "CheckboxField",
        defaultValue: false,
        isVisible: x => x.fields.foldInAccordion,
    },
    {
        name: accordionFoldingContainerFields.accordionTitle,
        displayName: "Title",
        fieldType: "General",
        editorTemplate: "TextField",
        defaultValue: "",
        isVisible: x => x.fields.foldInAccordion,
    },
    {
        name: accordionFoldingContainerFields.hideWhenNoResults,
        displayName: "Hide When No Results",
        fieldType: "General",
        editorTemplate: "CheckboxField",
        defaultValue: true,
        isVisible: x => x.fields.foldInAccordion,
    },
];

export default AccordionFoldingContainer;
